import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type Player, type PitchCount, type RestStatus } from '../services/api';
import './RestStatusBadge';

@customElement('player-selector')
export class PlayerSelector extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Array })
  players: Player[] = [];

  @property({ type: String })
  selectedId: string | null = null;

  @property({ type: Object })
  pitchCounts: Map<string, PitchCount> = new Map();

  @property({ type: String })
  gameId: string | undefined;

  @state()
  private showAddPlayer = false;

  @state()
  private newPlayerName = '';

  @state()
  private newPlayerNumber: number | null = null;

  @state()
  private newPlayerDob = '';

  @state()
  private adding = false;

  @state()
  private restStatuses: Map<string, RestStatus> = new Map();

  @state()
  private loadingRestStatus = false;

  @state()
  private searchQuery = '';

  @property({ type: Boolean })
  showRestStatus = true;

  @property({ type: Object })
  currentInningPitchers: Set<string> = new Set();

  @property({ type: Boolean })
  viewOnly = false;

  @state()
  private apTab: 'search' | 'create' = 'search';

  @state()
  private apSearchQuery = '';

  @state()
  private apSearchResults: Player[] = [];

  @state()
  private apSearching = false;

  private apSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Cache key for the last successful rest-status fetch. Used to dedup
  // updated() fires that carry a fresh `players` array reference but the
  // same set of player IDs — Lit triggers updated() whenever the property
  // reference changes, which happens on every parent render when the
  // parent computes `players` via Array.filter() (e.g. PitchCounter's
  // getActivePlayers). Without this guard, every parent render fired N
  // parallel /rest-status calls — confirmed root cause of the 2026-05-31
  // postgres CPU spike. See feedback memory for incident details.
  private lastRestStatusKey = '';

  async updated(changedProperties: Map<string, unknown>) {
    // Load rest statuses when players change
    if (changedProperties.has('players') && this.players.length > 0 && this.showRestStatus) {
      const key = this.players
        .map((p) => p.id)
        .sort()
        .join(',') + '|' + (this.gameId || '');
      if (key !== this.lastRestStatusKey) {
        this.lastRestStatusKey = key;
        await this.loadRestStatuses();
      }
    }
  }

  private async loadRestStatuses() {
    if (!this.gameId || this.players.length === 0) return;
    // Defensive: never run two batches in parallel. The content-key check
    // in updated() should already prevent the common case, but guard the
    // fetch path itself so a future caller can't bypass it.
    if (this.loadingRestStatus) return;

    this.loadingRestStatus = true;
    try {
      // Load rest status for all players in parallel
      const statuses = await Promise.all(
        this.players.map(async (player) => {
          try {
            const status = await api.getPlayerRestStatus(player.id, undefined, this.gameId);
            return { playerId: player.id, status };
          } catch {
            return { playerId: player.id, status: null };
          }
        })
      );

      // Update the map
      this.restStatuses = new Map(
        statuses
          .filter((s) => s.status !== null)
          .map((s) => [s.playerId, s.status as RestStatus])
      );
    } catch (error) {
      console.error('Failed to load rest statuses:', error);
    } finally {
      this.loadingRestStatus = false;
    }
  }

  private handleApSearch(query: string) {
    this.apSearchQuery = query;
    if (this.apSearchDebounceTimer) clearTimeout(this.apSearchDebounceTimer);
    if (query.length < 2) {
      this.apSearchResults = [];
      return;
    }
    this.apSearchDebounceTimer = setTimeout(async () => {
      this.apSearching = true;
      try {
        this.apSearchResults = await api.searchPlayers(query);
      } catch {
        this.apSearchResults = [];
      } finally {
        this.apSearching = false;
      }
    }, 300);
  }

  private async selectExistingPlayer(player: Player) {
    if (!this.gameId) return;
    this.adding = true;
    try {
      // Add existing player to game as AP (creates an adhoc link)
      const created = await api.createAdHocPlayer(this.gameId, {
        name: player.name,
        jersey_number: player.jersey_number ?? undefined,
        date_of_birth: player.date_of_birth || undefined,
      });
      this.dispatchEvent(new CustomEvent<Player>('player-added', { detail: created }));
      this.resetAddForm();
    } catch (err) {
      console.error('Failed to add player:', err);
    } finally {
      this.adding = false;
    }
  }

  private selectPlayer(player: Player) {
    const restStatus = this.restStatuses.get(player.id);

    // Warn if player is not available due to rest requirements
    if (restStatus && restStatus.status !== 'available') {
      const daysRemaining = Math.max(0, Math.min(restStatus.rest_days_needed, restStatus.rest_days_needed - restStatus.rest_days_elapsed));
      const availDate = restStatus.available_date
        ? new Date(restStatus.available_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : null;
      const dateInfo = availDate ? `\nAvailable: ${availDate}` : '';
      const confirmed = confirm(
        `${player.name} requires ${daysRemaining} more rest day(s) and should not pitch today.${dateInfo}\n\nSelect anyway?`
      );
      if (!confirmed) return;
    }

    this.dispatchEvent(new CustomEvent<string>('player-selected', { detail: player.id }));
  }

  private async addAdhocPlayer() {
    if (!this.newPlayerName.trim() || !this.gameId) return;

    this.adding = true;
    try {
      const player = await api.createAdHocPlayer(this.gameId, {
        name: this.newPlayerName.trim(),
        jersey_number: this.newPlayerNumber || undefined,
        date_of_birth: this.newPlayerDob || undefined,
      });

      this.dispatchEvent(new CustomEvent<Player>('player-added', { detail: player }));
      this.resetAddForm();
    } catch (error) {
      console.error('Failed to add player:', error);
    } finally {
      this.adding = false;
    }
  }

  private resetAddForm() {
    this.showAddPlayer = false;
    this.newPlayerName = '';
    this.newPlayerNumber = null;
    this.newPlayerDob = '';
    this.apTab = 'search';
    this.apSearchQuery = '';
    this.apSearchResults = [];
  }

  private getFilteredPlayers(): Player[] {
    if (!this.searchQuery.trim()) {
      return this.players;
    }
    
    const query = this.searchQuery.toLowerCase().trim();
    return this.players.filter(player => {
      const nameMatch = player.name.toLowerCase().includes(query);
      const numberMatch = player.jersey_number?.toString().includes(query);
      return nameMatch || numberMatch;
    });
  }

  render() {
    return html`
      <div class="space-y-3">
        <div class="flex justify-between items-center">
          <h3 class="font-semibold">Players</h3>
          ${this.viewOnly ? nothing : html`
            <button
              class="btn btn-sm btn-ghost text-primary gap-1"
              @click=${() => this.showAddPlayer = !this.showAddPlayer}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              AP
            </button>
          `}
        </div>

        <!-- Add player form -->
        ${!this.viewOnly && this.showAddPlayer ? html`
          <div class="card bg-base-200 p-4 animate-slide-up">
            <!-- Tabs -->
            <div class="tabs tabs-boxed mb-3">
              <button class="tab tab-sm ${this.apTab === 'search' ? 'tab-active' : ''}" @click=${() => { this.apTab = 'search'; }}>Find Player</button>
              <button class="tab tab-sm ${this.apTab === 'create' ? 'tab-active' : ''}" @click=${() => { this.apTab = 'create'; }}>Create New</button>
            </div>

            ${this.apTab === 'search' ? html`
              <!-- Search existing players -->
              <div class="flex flex-col gap-2">
                <input
                  type="text"
                  class="input input-bordered input-sm w-full"
                  placeholder="Search players by name..."
                  .value=${this.apSearchQuery}
                  @input=${(e: Event) => this.handleApSearch((e.target as HTMLInputElement).value)}
                />
                ${this.apSearching ? html`<div class="text-center py-2"><span class="loading loading-spinner loading-sm"></span></div>` : nothing}
                ${this.apSearchResults.length > 0 ? html`
                  <div class="max-h-48 overflow-y-auto space-y-1">
                    ${this.apSearchResults.map(p => html`
                      <button
                        class="btn btn-ghost btn-sm w-full justify-start gap-2 text-left"
                        @click=${() => this.selectExistingPlayer(p)}
                        ?disabled=${this.adding}
                      >
                        <span class="font-bold tabular-nums text-primary">${p.jersey_number != null ? `#${p.jersey_number}` : '--'}</span>
                        <span class="flex-1 truncate">${p.name}</span>
                        ${p.team_name ? html`<span class="text-xs text-base-content/50">${p.team_name}</span>` : nothing}
                      </button>
                    `)}
                  </div>
                ` : this.apSearchQuery.length >= 2 && !this.apSearching ? html`
                  <p class="text-xs text-base-content/50 text-center py-2">No players found. Try "Create New" tab.</p>
                ` : nothing}
                <button class="btn btn-ghost btn-sm" @click=${this.resetAddForm}>Cancel</button>
              </div>
            ` : html`
              <!-- Create new player -->
              <div class="flex flex-col gap-3">
                <div class="flex gap-2">
                  <input
                    type="text"
                    class="input input-bordered flex-1 min-w-0"
                    placeholder="Player name"
                    .value=${this.newPlayerName}
                    @input=${(e: Event) => this.newPlayerName = (e.target as HTMLInputElement).value}
                    @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.addAdhocPlayer()}
                  />
                  <input
                    type="number"
                    class="input input-bordered w-16 flex-shrink-0"
                    placeholder="#"
                    min="0"
                    max="99"
                    .value=${this.newPlayerNumber?.toString() ?? ''}
                    @input=${(e: Event) => {
                      const val = (e.target as HTMLInputElement).value;
                      this.newPlayerNumber = val ? parseInt(val, 10) : null;
                    }}
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-sm text-base-content/70">Birthdate (for pitch limits)</label>
                  <input
                    type="date"
                    class="input input-bordered"
                    .value=${this.newPlayerDob}
                    @input=${(e: Event) => this.newPlayerDob = (e.target as HTMLInputElement).value}
                  />
                </div>
                <div class="flex gap-2">
                  <button
                    class="btn btn-ghost flex-1"
                    @click=${this.resetAddForm}
                  >
                    Cancel
                  </button>
                  <button
                    class="btn btn-primary flex-1"
                    @click=${this.addAdhocPlayer}
                    ?disabled=${!this.newPlayerName.trim() || this.adding}
                  >
                    ${this.adding ? html`<span class="loading loading-spinner loading-sm"></span>` : html`
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Add
                    `}
                  </button>
                </div>
              </div>
            `}
          </div>
        ` : nothing}

        <!-- Search filter -->
        ${this.players.length > 5 ? html`
          <div class="relative">
            <input
              type="text"
              class="input input-bordered input-sm w-full pl-9"
              placeholder="Search by name or #..."
              .value=${this.searchQuery}
              @input=${(e: Event) => this.searchQuery = (e.target as HTMLInputElement).value}
            />
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            ${this.searchQuery ? html`
              <button
                class="btn btn-ghost btn-xs btn-circle absolute right-2 top-1/2 -translate-y-1/2"
                @click=${() => this.searchQuery = ''}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ` : nothing}
          </div>
        ` : nothing}

        <!-- Player list -->
        ${this.players.length === 0 ? html`
          <p class="text-center text-base-content/70 py-4">
            No players yet. Add a player to start counting.
          </p>
        ` : this.getFilteredPlayers().length === 0 ? html`
          <p class="text-center text-base-content/70 py-4">
            No players match "${this.searchQuery}"
          </p>
        ` : html`
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            ${this.getFilteredPlayers().map((player) => {
              const pitchCount = this.pitchCounts.get(player.id)?.count ?? 0;
              const isSelected = this.selectedId === player.id;
              const restStatus = this.restStatuses.get(player.id);
              const isResting = restStatus && restStatus.status !== 'available';

              return html`
                <button
                  class="player-card ${isSelected ? 'selected' : ''} ${isResting ? 'opacity-60' : ''}"
                  @click=${() => this.selectPlayer(player)}
                >
                  <div class="flex flex-col items-center gap-1">
                    ${player.jersey_number != null ? html`
                      <span class="text-2xl font-bold text-primary tabular-nums leading-none">#${player.jersey_number}</span>
                    ` : html`<span class="text-lg text-base-content/30 leading-none">--</span>`}
                    <span class="font-semibold truncate max-w-full text-sm">${player.name}</span>
                    <div class="badge ${pitchCount > 0 ? 'badge-primary' : 'badge-ghost'}">
                      ${pitchCount} pitches
                    </div>
                    ${this.currentInningPitchers.has(player.id)
                      ? html`<span class="badge badge-accent badge-xs">pitching</span>`
                      : nothing}
                    ${this.showRestStatus && restStatus
                      ? html`<rest-status-badge .status=${restStatus} compact></rest-status-badge>`
                      : nothing}
                    ${this.showRestStatus && this.loadingRestStatus && !restStatus
                      ? html`<span class="loading loading-spinner loading-xs"></span>`
                      : nothing}
                    ${player.is_adhoc ? html`
                      <span class="text-xs text-base-content/50">AP</span>
                    ` : nothing}
                  </div>
                </button>
              `;
            })}
          </div>
        `}
      </div>
    `;
  }
}
